// ─────────────────────────────────────────────────────────────────────────────
// tcxExport.js — Generates a Training Center XML (TCX) file for a workout.
//
// TCX is the path-of-least-resistance for getting CrewZ workouts into
// third-party platforms. Intervals.icu, Strava, and TrainingPeaks all
// natively accept .tcx uploads, and unlike the FIT binary format it's
// plain XML so no extra encoding library is needed.
//
// Caveats for rowing:
//   - TCX schema doesn't have a native "Rowing" sport — we use Sport="Other"
//     and embed the boat type into the activity Notes / Name. Intervals.icu
//     and Strava both let the rower re-classify after upload.
//   - "Cadence" carries stroke rate (spm). Range is 0–254 per spec.
//   - GPS Position is optional — erg sessions and any workout with empty
//     gps_data will emit Track points without <Position>.
// ─────────────────────────────────────────────────────────────────────────────

const TCX_NS = 'http://www.garmin.com/xmlschemas/TrainingCenterDatabase/v2';
const TCX_SCHEMA_LOCATION = 'http://www.garmin.com/xmlschemas/TrainingCenterDatabase/v2 https://www.garmin.com/xmlschemas/TrainingCenterDatabasev2.xsd';

const xmlEscape = (s) => String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

const isoFromMs = (ms) => new Date(ms).toISOString();

/**
 * Build TCX XML for a single workout.
 *
 * @param {object} workout
 *   @param {Date} workout.startTime          — when the workout began
 *   @param {number} workout.duration         — seconds
 *   @param {number} workout.distance         — meters
 *   @param {number} [workout.calories]
 *   @param {number} [workout.avgHeartRate]
 *   @param {number} [workout.maxHeartRate]
 *   @param {number} [workout.avgStrokeRate]
 *   @param {number} [workout.avgWatts]
 *   @param {string} [workout.sessionName]    — e.g. "2K Target"
 *   @param {string} [workout.boatType]       — e.g. "1x"
 *   @param {Array}  [workout.heartRateData]  — [{ timestamp, hr }]
 *   @param {Array}  [workout.gpsData]        — [{ timestamp, lat, lng, speed }]
 *
 * @returns {string} TCX XML text
 */
export function generateTcxFile(workout) {
  const {
    startTime,
    duration,
    distance,
    calories = 0,
    avgHeartRate,
    maxHeartRate,
    avgStrokeRate,
    avgWatts,
    sessionName = 'CREWZ Workout',
    boatType = '',
    heartRateData = [],
    gpsData = [],
    laps = [],
  } = workout;

  if (!(startTime instanceof Date) || Number.isNaN(startTime.getTime())) {
    throw new Error('generateTcxFile: startTime must be a valid Date');
  }
  const startMs = startTime.getTime();
  const startIso = startTime.toISOString();
  const totalSeconds = Math.max(1, Number(duration) || 0);
  const totalMeters = Math.max(0, Number(distance) || 0);

  // Stitch HR + GPS streams into a single sorted Trackpoint timeline. Each
  // sample is keyed by timestamp (ms epoch). We round to whole seconds so
  // duplicate ticks merge cleanly.
  const pointsByMs = new Map();
  const addPoint = (ms, patch) => {
    const key = Math.floor(ms / 1000) * 1000;
    const prev = pointsByMs.get(key) || { ms: key };
    pointsByMs.set(key, { ...prev, ...patch });
  };

  for (const hr of heartRateData) {
    if (hr && hr.timestamp && hr.hr != null) addPoint(hr.timestamp, { hr: hr.hr });
  }
  for (const g of gpsData) {
    if (!g || !g.timestamp) continue;
    addPoint(g.timestamp, {
      lat: typeof g.lat === 'number' ? g.lat : null,
      lng: typeof g.lng === 'number' ? g.lng : null,
      speed: typeof g.speed === 'number' ? g.speed : null,
    });
  }

  // If nothing was logged (PM5/ergo session without GPS or HR), inject a
  // minimal two-point track so Intervals.icu / Strava still accept the file.
  if (pointsByMs.size === 0) {
    addPoint(startMs, {});
    addPoint(startMs + totalSeconds * 1000, {});
  }

  // Sort & cumulate distance linearly across the timeline (good enough for
  // erg sessions; on-water GPS distance comes from the Position deltas
  // upstream of TCX consumers like Intervals).
  const sortedPoints = Array.from(pointsByMs.values()).sort((a, b) => a.ms - b.ms);
  const minMs = sortedPoints[0].ms;
  const maxMs = sortedPoints[sortedPoints.length - 1].ms;
  const span = Math.max(1, maxMs - minMs);

  const trackpointXml = (p) => {
    const fracDist = ((p.ms - minMs) / span) * totalMeters;
    const lines = [];
    lines.push(`      <Trackpoint>`);
    lines.push(`        <Time>${isoFromMs(p.ms)}</Time>`);
    if (p.lat != null && p.lng != null) {
      lines.push(`        <Position>`);
      lines.push(`          <LatitudeDegrees>${p.lat}</LatitudeDegrees>`);
      lines.push(`          <LongitudeDegrees>${p.lng}</LongitudeDegrees>`);
      lines.push(`        </Position>`);
    }
    lines.push(`        <DistanceMeters>${fracDist.toFixed(2)}</DistanceMeters>`);
    if (p.hr != null) {
      lines.push(`        <HeartRateBpm><Value>${Math.round(p.hr)}</Value></HeartRateBpm>`);
    }
    // Cadence — average stroke rate is the best per-point value we can offer
    // without per-stroke timestamps. TCX caps cadence at 254.
    if (typeof avgStrokeRate === 'number' && avgStrokeRate > 0) {
      lines.push(`        <Cadence>${Math.min(254, Math.round(avgStrokeRate))}</Cadence>`);
    }
    lines.push(`      </Trackpoint>`);
    return lines.join('\n');
  };
  const distAt = (ms) => ((Math.min(maxMs, Math.max(minMs, ms)) - minMs) / span) * totalMeters;

  const notesParts = [];
  if (boatType) notesParts.push(`Boat: ${boatType}`);
  if (avgWatts) notesParts.push(`Avg power: ${Math.round(avgWatts)} W`);
  notesParts.push('Exported from CREWZ');
  const notes = xmlEscape(notesParts.join(' · '));
  const name = xmlEscape(sessionName);

  const avgHrLine = avgHeartRate
    ? `      <AverageHeartRateBpm><Value>${Math.round(avgHeartRate)}</Value></AverageHeartRateBpm>\n`
    : '';
  const maxHrLine = maxHeartRate
    ? `      <MaximumHeartRateBpm><Value>${Math.round(maxHeartRate)}</Value></MaximumHeartRateBpm>\n`
    : '';
  const cadenceLine = (typeof avgStrokeRate === 'number' && avgStrokeRate > 0)
    ? `      <Cadence>${Math.min(254, Math.round(avgStrokeRate))}</Cadence>\n`
    : '';

  // Laps: pieces (start_offset_s → end_offset_s) plus the gaps between them,
  // so the whole session stays one activity. No pieces → one lap.
  const windows = [];
  const pieceLaps = (laps || [])
    .filter((l) => Number.isFinite(l.start_offset_s) && Number.isFinite(l.end_offset_s) && l.end_offset_s > l.start_offset_s)
    .sort((a, b) => a.start_offset_s - b.start_offset_s);
  let cursor = 0;
  for (const l of pieceLaps) {
    const s0 = Math.max(cursor, Math.min(totalSeconds, l.start_offset_s));
    const s1 = Math.min(totalSeconds, l.end_offset_s);
    if (s1 <= s0) continue;
    if (s0 > cursor) windows.push({ s0: cursor, s1: s0 });
    windows.push({ s0, s1, piece: l });
    cursor = s1;
  }
  if (cursor < totalSeconds || !windows.length) windows.push({ s0: cursor, s1: totalSeconds });

  const lapsXml = windows.map((w, i) => {
    const w0 = startMs + w.s0 * 1000;
    const w1 = startMs + w.s1 * 1000;
    const last = i === windows.length - 1;
    const pts = sortedPoints.filter((p) => p.ms >= w0 && (last ? p.ms <= w1 : p.ms < w1));
    const lapMeters = w.piece && Number.isFinite(w.piece.distance_m) ? w.piece.distance_m : distAt(w1) - distAt(w0);
    const lapCadence = w.piece?.avg_stroke_rate > 0 ? `        <Cadence>${Math.min(254, Math.round(w.piece.avg_stroke_rate))}</Cadence>\n` : cadenceLine;
    const lapCalories = windows.length === 1 ? Math.round(calories) : Math.round((calories * (w.s1 - w.s0)) / totalSeconds);
    const lapNotes = w.piece ? xmlEscape(`Piece ${w.piece.piece_number}: ${w.piece.piece_name || w.piece.piece_type}`) : notes;
    return `      <Lap StartTime="${isoFromMs(w0)}">
        <TotalTimeSeconds>${(w.s1 - w.s0).toFixed(1)}</TotalTimeSeconds>
        <DistanceMeters>${Math.max(0, lapMeters).toFixed(1)}</DistanceMeters>
        <Calories>${lapCalories}</Calories>
${avgHrLine}${maxHrLine}        <Intensity>${w.piece || windows.length === 1 ? 'Active' : 'Resting'}</Intensity>
${lapCadence}        <TriggerMethod>Manual</TriggerMethod>
        <Track>
${pts.map(trackpointXml).join('\n')}
        </Track>
        <Notes>${lapNotes}</Notes>
      </Lap>`;
  }).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<TrainingCenterDatabase xmlns="${TCX_NS}" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="${TCX_SCHEMA_LOCATION}">
  <Activities>
    <Activity Sport="Other">
      <Id>${startIso}</Id>
${lapsXml}
      <Notes>${name} - ${notes}</Notes>
      <Creator xsi:type="Device_t">
        <Name>CREWZ</Name>
        <UnitId>1</UnitId>
        <ProductID>1</ProductID>
        <Version>
          <VersionMajor>1</VersionMajor>
          <VersionMinor>0</VersionMinor>
          <BuildMajor>0</BuildMajor>
          <BuildMinor>0</BuildMinor>
        </Version>
      </Creator>
    </Activity>
  </Activities>
  <Author xsi:type="Application_t">
    <Name>CREWZ Rowing Technology</Name>
    <Build>
      <Version>
        <VersionMajor>1</VersionMajor>
        <VersionMinor>0</VersionMinor>
        <BuildMajor>0</BuildMajor>
        <BuildMinor>0</BuildMinor>
      </Version>
    </Build>
    <LangID>EN</LangID>
    <PartNumber>000-00000-00</PartNumber>
  </Author>
</TrainingCenterDatabase>`;
}

/**
 * Convenience: trigger a browser download for a single workout's TCX file.
 * Used by the Download button on WorkoutHistory + WorkoutMonitorV2.
 */
export function downloadTcxFile(workout, filename) {
  const xml = generateTcxFile(workout);
  const blob = new Blob([xml], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `crewz-workout-${new Date(workout.startTime).toISOString().split('T')[0]}.tcx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
